/**
 * Detect an authenticated SPA session without reading httpOnly cookies.
 * access_token / refresh_token cookies are not visible to document.cookie.
 *
 * The persisted Zustand `isAuthenticated` flag alone is spoofable via localStorage,
 * so cookie-mode detection also requires a CSRF cache entry (set after a real
 * API bootstrap such as /auth/me or ensureCsrfToken). secureTokenManager.getToken()
 * is always null under cookie mode — kept only for legacy bearer fallback.
 */

import { useAuthStore } from "../stores/authStore";
import { STORAGE_KEYS } from "./constants";
import { getCachedCsrfToken } from "./csrfToken";
import { secureTokenManager } from "./secureTokenManager";

function readPersistedAuthFlag(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH_STORAGE);
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as {
      state?: { isAuthenticated?: boolean };
      isAuthenticated?: boolean;
    };
    const state = parsed?.state ?? parsed;
    return state?.isAuthenticated === true;
  } catch {
    // Ignore corrupt persist JSON
    return false;
  }
}

function hasAuthFlag(): boolean {
  if (readPersistedAuthFlag()) {
    return true;
  }
  try {
    // Fresh login: persist may lag briefly behind in-memory store.
    return useAuthStore.getState().isAuthenticated === true;
  } catch {
    return false;
  }
}

export function hasAuthenticatedSession(): boolean {
  try {
    if (secureTokenManager.getToken()) {
      return true;
    }
  } catch {
    // Ignore token manager errors
  }

  // Cookie mode: require auth flag + CSRF bootstrap evidence (not flag alone).
  let csrf: string | null = null;
  try {
    csrf = getCachedCsrfToken();
  } catch {
    csrf = null;
  }
  if (!csrf) {
    return false;
  }

  return hasAuthFlag();
}
